/**
 * LR_Cross — chart overlay for "a closed candle crosses the LR body".
 *
 * THE RULE lives in `lrCrossEngine.ts`, a byte-identical twin of the backend
 * copy the scheduled scan runs (`scripts/check-engine-parity.mjs`). This file
 * builds the two inputs exactly as the scan does — LR bodies from the RAW
 * candles at the CHART's LR length, and the Regular or HA close — and turns the
 * steps into chart marks. A mark here and an alert from the scan cannot come
 * from different logic.
 *
 * SOURCE. The chart's marks use Chart Settings → LR_Cross → source candle; the
 * scan uses the strategy's own setting (owner, 2026-11-01). When the chart is
 * not DRAWING the source candle, or the LR chip is off, the marks are still
 * right but the crossing is not visible, so the overlay says so.
 *
 * REPAINT. `candles` are the pane's CLOSED bars; a body at bar i is fitted on
 * bars <= i, so a marked bar never moves. Nothing is marked on the forming bar.
 */
import { CandleRow } from '../../components/charts/TradingChart';
import { ChartStrategy, ChartStrategyOptions, StrategyOverlayResult, ChartMarker, StrategyNote } from './types';
import { computeClnBars } from '../clnCandles';
import { transformCandles } from '../candleTransforms';
import {
  computeLrCrossSteps, lrCrossWarmup, lrCrossActionLabel, resolveLrCrossSource, resolveLrCrossLength,
  LR_CROSS_OPTION_HINT,
} from './lrCrossEngine';

const MARK_BUY = '#00C853';
// Brighter than OM_Simple's #D50000, whose label text was hard to read on the dark
// theme (probe 2026-11-02); #FF5252 still reads on white.
const MARK_SHORT = '#FF5252';

const fmt = (v: number) => {
  const a = Math.abs(v);
  const dp = a >= 1000 ? 1 : a >= 1 ? 2 : a >= 0.01 ? 4 : 6;
  return v.toFixed(dp);
};

export function computeLrCrossOverlay(candles: CandleRow[], opts?: ChartStrategyOptions): StrategyOverlayResult {
  if (!candles || candles.length < 2) return { markers: [] };
  const length = resolveLrCrossLength(opts?.chartClnLength);
  const source = resolveLrCrossSource(opts?.lrCrossSource);
  const warm = lrCrossWarmup(length);

  if (candles.length <= warm) {
    return {
      markers: [],
      notice: `LR_Cross needs more than ${warm} closed bars (LR ${length}); this chart has ${candles.length}.`,
    };
  }

  const lr = computeClnBars(candles, length);
  const src = source === 'HA' ? transformCandles(candles, 'HA').map(c => c.close) : candles.map(c => c.close);
  const steps = computeLrCrossSteps(lr, src, { length });

  const markers: ChartMarker[] = [];
  const signalsByTime: NonNullable<StrategyOverlayResult['signalsByTime']> = new Map();
  const positionByTime: NonNullable<StrategyOverlayResult['positionByTime']> = new Map();
  const notesByTime: NonNullable<StrategyOverlayResult['notesByTime']> = new Map();

  for (const st of steps) {
    positionByTime.set(st.time, st.pos);
    if (!st.signal) continue;
    const buy = st.signal === 'BUY';
    const suffix = st.closes ? ' ✕' + (st.closes === 'LONG' ? 'L' : 'S') : st.reCross ? ' re' : '';
    markers.push({
      time: st.time, position: buy ? 'belowBar' : 'aboveBar', text: (buy ? 'B' : 'S') + suffix,
      color: buy ? MARK_BUY : MARK_SHORT, shape: buy ? 'arrowUp' : 'arrowDown', size: 2,
    });
    signalsByTime.set(st.time, { signal: st.signal, strength: null, pos: st.pos, reason: st.reason });
    const edge = buy ? st.upper : st.lower;
    const note: StrategyNote = {
      code: 'X',
      label: `LR_Cross ${lrCrossActionLabel(st)}`,
      tip: `${source} close ${fmt(st.src)} ${buy ? 'above the LR body top' : 'below the LR body bottom'} ${fmt(edge)} `
        + `(LR ${length}).`
        + (st.closes ? ` Also exits the open ${st.closes === 'LONG' ? 'long' : 'short'}.` : '')
        + (st.reCross ? ` Already ${buy ? 'long' : 'short'}: price re-crossed the same way.` : '')
        + ` Option idea: ${LR_CROSS_OPTION_HINT[st.signal]}. No stop or target is set for you.`,
      color: buy ? MARK_BUY : MARK_SHORT,
    };
    notesByTime.set(st.time, [note]);
  }

  // One SHORT line per row: the badge draws every row unwrapped.
  const notes: string[] = [];
  const drawn = opts?.lrCrossChartCandleType;
  const drawnSource = drawn === 'HA' ? 'HA' : (drawn === 'Regular' || drawn === 'CLN') ? 'Regular' : drawn;
  if (drawnSource && drawnSource !== source) {
    notes.push(`LR_Cross marks use ${source} closes; the chart draws ${drawn} candles.`);
    notes.push(`Switch the candle type to ${source} to see the crossing closes.`);
  }
  if (opts?.lrCrossLrVisible === false) {
    notes.push('Turn on the LR chip to see the body the crosses are measured against.');
  }

  return {
    markers,
    signalsByTime,
    positionByTime,
    notesByTime: notesByTime.size ? notesByTime : undefined,
    notice: notes.length ? notes.join('\n') : undefined,
  };
}

export const lrCrossStrategy: ChartStrategy = {
  id: 'lr_cross',
  name: 'LR_Cross',
  computeOverlays(candles: CandleRow[], opts?: ChartStrategyOptions): StrategyOverlayResult {
    return computeLrCrossOverlay(candles, opts);
  },
};
